import { SCOPES } from '../scopes.js';
import { zoneSetting } from '../types.js';

/** Czytelny po polsku opis czasu dla dozwolonych wartości `challenge_ttl`. */
const TTL_PL: Readonly<Record<number, string>> = {
  300: '5 minut',
  900: '15 minut',
  1800: '30 minut',
  2700: '45 minut',
  3600: 'godzinę',
  7200: '2 godziny',
  10800: '3 godziny',
  14400: '4 godziny',
  28800: '8 godzin',
  57600: '16 godzin',
  86400: 'dobę',
  604800: 'tydzień',
  2592000: '30 dni',
  31536000: 'rok',
};

export const ssl = zoneSetting({
  settingId: 'ssl',
  value: { t: 'enum', values: ['off', 'flexible', 'full', 'strict'] },
  staff: {
    label: 'Tryb SSL',
    hint: 'Sposób szyfrowania połączenia między Cloudflare a serwerem klienta.',
  },
  customer: {
    changePl: (v) =>
      v === 'strict'
        ? 'Włączymy pełne, weryfikowane szyfrowanie połączenia z serwerem.'
        : v === 'full'
          ? 'Włączymy szyfrowanie połączenia z serwerem.'
          : v === 'flexible'
            ? 'Zmienimy sposób szyfrowania połączenia.'
            : 'Wyłączymy szyfrowanie połączenia.',
  },
  plans: { min: 'free' },
  scopes: [SCOPES.zoneSettingsWrite],
  recommended: 'full',
  valueNotes: {
    flexible: {
      severity: 'blocking',
      textPl:
        'Tryb „flexible" jest źródłem pętli przekierowań i pozorne szyfrowanie ' +
        'kończy się na Cloudflare. Nie pozwalamy go zapisać w presecie.',
    },
    off: {
      severity: 'warn',
      textPl: 'Całkowite wyłączenie szyfrowania — stosować tylko świadomie.',
    },
  },
});

export const alwaysUseHttps = zoneSetting({
  settingId: 'always_use_https',
  value: { t: 'enum', values: ['on', 'off'] },
  staff: { label: 'Wymuszaj HTTPS', hint: 'Przekierowuje cały ruch HTTP na HTTPS.' },
  customer: {
    changePl: (v) =>
      v === 'on'
        ? 'Sprawimy, że strona zawsze otworzy się przez bezpieczne połączenie.'
        : 'Przestaniemy wymuszać bezpieczne połączenie.',
  },
  plans: { min: 'free' },
  scopes: [SCOPES.zoneSettingsWrite],
  recommended: 'on',
});

export const automaticHttpsRewrites = zoneSetting({
  settingId: 'automatic_https_rewrites',
  value: { t: 'enum', values: ['on', 'off'] },
  staff: {
    label: 'Automatyczna korekta odnośników',
    hint: 'Podmienia odnośniki http:// na https:// w treści strony.',
  },
  customer: {
    changePl: (v) =>
      v === 'on'
        ? 'Naprawimy odnośniki, które prowadzą do niezabezpieczonej wersji strony.'
        : 'Przestaniemy naprawiać odnośniki do niezabezpieczonej wersji strony.',
  },
  plans: { min: 'free' },
  scopes: [SCOPES.zoneSettingsWrite],
  recommended: 'on',
});

export const minTlsVersion = zoneSetting({
  settingId: 'min_tls_version',
  value: { t: 'enum', values: ['1.0', '1.1', '1.2', '1.3'] },
  staff: {
    label: 'Minimalna wersja TLS',
    hint: 'Połączenia starszym protokołem będą odrzucane. 1.2 to bezpieczny domyślny wybór.',
  },
  customer: {
    changePl: (v) =>
      v === '1.0' || v === '1.1'
        ? 'Dopuścimy starsze, słabsze sposoby szyfrowania.'
        : 'Odrzucimy połączenia używające przestarzałych sposobów szyfrowania.',
  },
  plans: { min: 'free' },
  scopes: [SCOPES.zoneSettingsWrite],
  recommended: '1.2',
  valueNotes: {
    '1.3': {
      severity: 'warn',
      textPl: 'Bardzo stare urządzenia i niektóre integracje mogą stracić dostęp.',
    },
  },
});

export const securityLevel = zoneSetting({
  settingId: 'security_level',
  value: {
    t: 'enum',
    values: ['off', 'essentially_off', 'low', 'medium', 'high', 'under_attack'],
  },
  staff: {
    label: 'Poziom bezpieczeństwa',
    hint: 'Dziś realnie działa jak przełącznik trybu ataku. Patrz ostrzeżenie o wartościach.',
  },
  customer: {
    changePl: (v) =>
      v === 'under_attack'
        ? 'Włączymy tryb wzmożonej ochrony — każdy odwiedzający przejdzie krótką weryfikację.'
        : v === 'off' || v === 'essentially_off'
          ? 'Zmniejszymy liczbę weryfikacji pokazywanych odwiedzającym.'
          : 'Dostosujemy poziom ochrony przed podejrzanym ruchem.',
  },
  plans: { min: 'free' },
  scopes: [SCOPES.zoneSettingsWrite],
  recommended: 'off',
  // Pełny enum zostaje, bo snapshot musi umieć odwzorować to, co strefa już ma.
  deprecated: {
    sincePl:
      'Wartości „low", „medium" i „high" nie mają dziś realnego efektu — threat score ' +
      'jest u Cloudflare zawsze równy 0. Ustawienie działa praktycznie jak przełącznik ' +
      'trybu ataku (under_attack) i jego wyłączenie.',
  },
  valueNotes: {
    under_attack: {
      severity: 'warn',
      textPl:
        'Tryb ataku wstawia weryfikację przed KAŻDYM żądaniem HTTP. Psuje API, webhooki ' +
        '(w tym callbacki płatności), aplikacje mobilne i integracje kurierskie. ' +
        'Wymaga ustawienia automatycznego cofnięcia.',
    },
    low: { severity: 'info', textPl: 'Bez realnego efektu — threat score zawsze 0.' },
    medium: { severity: 'info', textPl: 'Bez realnego efektu — threat score zawsze 0.' },
    high: { severity: 'info', textPl: 'Bez realnego efektu — threat score zawsze 0.' },
  },
});

export const browserCheck = zoneSetting({
  settingId: 'browser_check',
  value: { t: 'enum', values: ['on', 'off'] },
  staff: {
    label: 'Kontrola integralności przeglądarki',
    hint: 'Blokuje żądania z nagłówkami typowymi dla botów.',
  },
  customer: {
    changePl: (v) =>
      v === 'on'
        ? 'Zablokujemy ruch, który nie wygląda na prawdziwą przeglądarkę.'
        : 'Przestaniemy blokować ruch nieprzypominający przeglądarki.',
  },
  plans: { min: 'free' },
  scopes: [SCOPES.zoneSettingsWrite],
  recommended: 'on',
});

export const challengeTtl = zoneSetting({
  settingId: 'challenge_ttl',
  value: {
    t: 'intEnum',
    values: [
      300, 900, 1800, 2700, 3600, 7200, 10800, 14400, 28800, 57600, 86400, 604800, 2592000,
      31536000,
    ],
  },
  staff: {
    label: 'Ważność weryfikacji',
    hint: 'Jak długo odwiedzający nie zobaczy ponownie weryfikacji. Zalecane 15–45 minut.',
  },
  customer: {
    changePl: (v) =>
      `Osoba, która raz przejdzie weryfikację, nie zobaczy jej ponownie przez ${
        TTL_PL[v] ?? `${v} s`
      }.`,
  },
  plans: { min: 'free' },
  scopes: [SCOPES.zoneSettingsWrite],
  recommended: 1800,
  // Ma znaczenie tylko wtedy, gdy cokolwiek faktycznie wystawia weryfikację.
  dependsOn: ['security_level', 'browser_check'],
});

export const brotli = zoneSetting({
  settingId: 'brotli',
  value: { t: 'enum', values: ['on', 'off'] },
  staff: {
    label: 'Kompresja Brotli',
    hint: 'Działa, ale nowszą drogą są Compression Rules (dają też Zstandard).',
  },
  customer: {
    changePl: (v) =>
      v === 'on'
        ? 'Włączymy kompresję, dzięki której strona wczyta się szybciej.'
        : 'Wyłączymy dodatkową kompresję strony.',
  },
  plans: { min: 'free' },
  scopes: [SCOPES.zoneSettingsWrite],
  recommended: 'on',
});
